import React, { useRef } from 'react';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Progress } from '../../../../../../../CommonComponents/OPL/Progress/Progress';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { MarginProps } from '../../../../../../../theme/styledProps';
import { truncateFileName } from '../../../../../../../CommonUtils/truncateFileName';
import { ProgressIcon, UploadFileVariant } from './UploadAgreement.styles';
import {
  iconMap,
  progressVariantMap,
  TRUNCATE_UPLOADING_FILE_NAME_THRESHOLD,
} from './UploadAgreement.meta';
import { UploadProgressRef, useUploadProgress } from '../../hooks/useUploadProgress';
import { useUploadAgreementMutation } from '../../api/useUploadAgreementMutation';
import { Tooltip } from '../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';

interface Props extends MarginProps {
  isFirm?: boolean;
  file: File;
  message: string;
  onUploadSuccess?(): void;
  variant: UploadFileVariant;
  isRejected?: boolean;
  progress?: number;
}

export const UploadAgreement: React.FC<Props> = ({
  isFirm,
  file,
  message,
  variant,
  progress = 0,
  isRejected,
  onUploadSuccess,
  ...rest
}) => {
  const uploadProgressRef = useRef<UploadProgressRef>(null);

  const { progressLabelRef, progressValueRef, progressBarRef, progressIcon } = useUploadProgress({
    uploadProgressRef,
  });

  const { mutateAsync: uploadAgreement } = useUploadAgreementMutation({
    isFirm,
    uploadProgressRef,
  });

  useEffectOnce(() => {
    (async () => {
      if (isRejected) {
        return;
      }

      try {
        await uploadAgreement({ file });

        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } catch (err) {
        console.error(err);
      }
    })();
  });

  return (
    <Box display="flex" alignItems="center" {...rest}>
      <Box display="flex" flex={1} alignItems="center">
        <Icon className="far fa-file-pdf" fontSize="h5" color="neutral.500" mr="8" />

        <Tooltip
          content={file.name}
          disabled={file.name.length <= TRUNCATE_UPLOADING_FILE_NAME_THRESHOLD}
          isOverflowWrap
          placement="top"
        >
          <Text isOverflowWrap fontWeight={500}>
            {truncateFileName(file.name, TRUNCATE_UPLOADING_FILE_NAME_THRESHOLD)}
          </Text>
        </Tooltip>
      </Box>

      <Box width={290} display="flex" alignItems="center" ml="16">
        <Box flex={1}>
          <Progress ref={progressBarRef} value={progress} variant={progressVariantMap[variant]} />

          <Box display="flex" justifyContent="space-between" mt="8">
            <Text
              ref={progressLabelRef}
              fontWeight={500}
              fontSize="small"
              color={isRejected ? 'error.500' : 'neutral.700'}
            >
              {message}
            </Text>

            <Text ref={progressValueRef} fontWeight={500} fontSize="small">
              {!isRejected ? `${progress}%` : ''}
            </Text>
          </Box>
        </Box>

        <ProgressIcon
          ref={progressIcon}
          variant={variant}
          className={iconMap[variant]}
          tabIndex={isRejected ? -1 : 0}
          role={!isRejected ? 'button' : undefined}
          // We use inline styles here because the className can be
          // overwritten imperatively to change the icon
          style={{ fontSize: 16, marginLeft: 16 }}
        />
      </Box>
    </Box>
  );
};
